/**
 * Overtime Reject Modal Komponenti
 * 
 * Fazla mesai talebini reddetmek için kullanılan modal komponenti.
 * Talep detaylarını gösterir ve red nedeni girmek için kullanıcıdan input alır.
 * 
 * Özellikler:
 * - Talep detaylarını gösterir
 * - Red nedeni girişi (zorunlu)
 * - Form validasyonu
 * - Reddet/İptal butonları
 * - Loading durumu
 */

import React, { useState, useEffect } from 'react';
import { Modal, Input, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { OvertimeDto } from '../../../../types/overtime.types';
import { OVERTIME_MODAL_MESSAGES } from '../../../../constants/overtimeConstants';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

const { TextArea } = Input;

interface OvertimeRejectModalProps {
  visible: boolean;
  overtime: OvertimeDto | null;
  onConfirm: (overtimeId: number, rejectionReason: string) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

/**
 * Fazla mesai reddetme modal komponenti
 * @param visible - Modal görünür mü?
 * @param overtime - Reddedilecek overtime kaydı
 * @param onConfirm - Reddetme callback'i (red nedeni ile)
 * @param onCancel - İptal callback'i
 * @param loading - Yükleniyor durumu
 */
const OvertimeRejectModal: React.FC<OvertimeRejectModalProps> = ({
  visible,
  overtime,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [rejectionReason, setRejectionReason] = useState('');

  // Mesaj konfigürasyonunu al
  const messages = OVERTIME_MODAL_MESSAGES.reject;

  // Modal kapandığında formu temizle
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setRejectionReason('');
    }
  }, [visible, form]);

  /**
   * Süreyi formatlar
   */
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins} ${t('overtime.minute', 'dakika')}`;
    }
    
    return mins > 0 
      ? `${hours} ${t('overtime.hour', 'saat')} ${mins} ${t('overtime.minute', 'dakika')}` 
      : `${hours} ${t('overtime.hour', 'saat')}`;
  };

  /**
   * Reddetme işlemini başlatır
   */
  const handleConfirm = async () => {
    try {
      // Form validasyonu yap
      await form.validateFields();
      
      if (overtime && rejectionReason.trim()) {
        await onConfirm(overtime.id, rejectionReason.trim());
      }
    } catch (error) {
      // Form validasyon hatası - işlem yapma
      console.error('Form validation failed:', error);
    }
  };

  if (!overtime) {
    return null;
  }

  return (
    <Modal
      title={
        <div className="d-flex align-items-center gap-2">
          <i className="ti ti-x text-danger" style={{ fontSize: '1.5rem' }} />
          <span>{i18n.language === 'en' ? messages.titleEn : messages.title}</span>
        </div>
      }
      open={visible}
      onOk={handleConfirm}
      onCancel={onCancel}
      confirmLoading={loading}
      okText={t('overtime.reject', 'Reddet')}
      cancelText={t('overtime.cancel', 'İptal')}
      okButtonProps={{ 
        danger: true,
        icon: <i className="ti ti-x me-1" />,
      }}
      cancelButtonProps={{
        disabled: loading,
      }}
      width={600}
      centered
    >
      {/* Talep Detayları */}
      <div className="mb-4">
        <div className="mb-3 pb-3 border-bottom">
          <div className="row">
            <div className="col-6 mb-2">
              <span className="text-muted d-block" style={{ fontSize: '0.875rem' }}>
                {t('overtime.employee', 'Çalışan')}
              </span>
              <strong>
                {overtime.user.firstName} {overtime.user.lastName}
              </strong>
            </div>
            <div className="col-6 mb-2">
              <span className="text-muted d-block" style={{ fontSize: '0.875rem' }}>
                {t('overtime.date', 'Tarih')}
              </span>
              <strong>
                {dayjs(overtime.date).locale(i18n.language).format('DD MMMM YYYY')}
              </strong>
            </div>
            <div className="col-6 mb-2">
              <span className="text-muted d-block" style={{ fontSize: '0.875rem' }}>
                {t('overtime.time', 'Saat')}
              </span>
              <strong>
                {overtime.overtimeStart} - {overtime.overtimeEnd}
              </strong>
            </div>
            <div className="col-6 mb-2">
              <span className="text-muted d-block" style={{ fontSize: '0.875rem' }}>
                {t('overtime.duration', 'Süre')}
              </span>
              <strong>
                {formatDuration(overtime.overtimeDuration)}
              </strong>
            </div>
          </div>
          {overtime.description && (
            <div className="mt-3">
              <span className="text-muted d-block mb-1" style={{ fontSize: '0.875rem' }}>
                {t('overtime.description', 'Açıklama')}
              </span>
              <p className="mb-0">{overtime.description}</p>
            </div>
          )}
        </div>

        {/* Red Nedeni Formu */}
        <Form form={form} layout="vertical">
          <Form.Item
            label={
              <span className="fw-medium">
                {i18n.language === 'en' ? messages.reasonLabelEn : messages.reasonLabel}
                {' '}
                <span className="text-danger">*</span>
              </span>
            }
            name="rejectionReason"
            rules={[
              {
                required: true,
                message: i18n.language === 'en' 
                  ? messages.reasonRequiredEn 
                  : messages.reasonRequired,
              },
              {
                min: 10,
                message: t('overtime.rejectionReasonMinLength', 'Red nedeni en az 10 karakter olmalıdır'),
              },
              {
                max: 500,
                message: t('overtime.rejectionReasonMaxLength', 'Red nedeni en fazla 500 karakter olabilir'),
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder={
                i18n.language === 'en' 
                  ? messages.reasonPlaceholderEn 
                  : messages.reasonPlaceholder
              }
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              maxLength={500}
              showCount
              disabled={loading}
            />
          </Form.Item>
        </Form>

        {/* Uyarı */}
        <div className="alert alert-warning py-2" role="alert">
          <div className="d-flex align-items-start gap-2">
            <i className="ti ti-alert-triangle" style={{ fontSize: '1.25rem' }} />
            <small>
              {t(
                'overtime.rejectionWarning',
                'Red nedeni çalışana iletilecektir. Lütfen net ve anlaşılır bir açıklama yazın.'
              )}
            </small>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OvertimeRejectModal;


